import React, { useState } from 'react';
import {
  Target, Plus, Calendar, Clock, CheckCircle, Edit2, Trash2,
  ChevronDown, ChevronUp, Sparkles, MessageSquare
} from 'lucide-react';
import { weeklyQuestions } from '../hooks/useGoals';

function Dashboard({
  goals,
  stats,
  timelineEvents,
  getGoalProgress,
  onCreateGoal,
  onEditGoal,
  onDeleteGoal,
  onToggleMilestone,
  onCheckin
}) {
  const [expandedGoal, setExpandedGoal] = useState(null);
  const [checkinText, setCheckinText] = useState('');
  const [currentQuestion] = useState(() =>
    weeklyQuestions[Math.floor(Math.random() * weeklyQuestions.length)]
  );

  const handleCheckinSubmit = () => {
    if (checkinText.trim()) {
      onCheckin({
        reflection: checkinText,
        question: currentQuestion
      });
      setCheckinText('');
    }
  };

  const toggleGoalExpand = (goalId) => {
    setExpandedGoal(expandedGoal === goalId ? null : goalId);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="dashboard-grid">
      <div className="goals-section">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              <Target size={20} />
              我的目标
            </h2>
            <button className="btn btn-primary btn-small" onClick={onCreateGoal}>
              <Plus size={16} />
              新建目标
            </button>
          </div>

          {goals.length === 0 ? (
            <div className="empty-state">
              <Target size={64} />
              <h3>开始你的目标之旅</h3>
              <p>创建你的第一个目标，开始追踪你的进步</p>
              <button className="btn btn-primary" onClick={onCreateGoal}>
                <Plus size={18} />
                创建目标
              </button>
            </div>
          ) : (
            <div className="goals-list">
              {goals.map(goal => {
                const progress = getGoalProgress(goal);
                const isExpanded = expandedGoal === goal.id;

                return (
                  <div key={goal.id} className="goal-item animate-in">
                    <div
                      className="goal-header"
                      onClick={() => toggleGoalExpand(goal.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div>
                        <h3 className="goal-title">{goal.title}</h3>
                        <span className="goal-category">{goal.category}</span>
                      </div>
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>

                    <p className="goal-description">{goal.description}</p>

                    <div className="progress-bar-container">
                      <div
                        className="progress-bar"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <div className="progress-info">
                      <span>{goal.milestones?.filter(m => m.completed).length || 0} / {goal.milestones?.length || 0} 里程碑</span>
                      <span>{progress}% 完成</span>
                    </div>

                    {isExpanded && goal.milestones && (
                      <div className="milestones-list">
                        {goal.milestones.map(milestone => (
                          <div key={milestone.id} className="milestone-item">
                            <div
                              className={`milestone-checkbox ${milestone.completed ? 'completed' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onToggleMilestone(goal.id, milestone.id);
                              }}
                            >
                              {milestone.completed && <CheckCircle size={14} />}
                            </div>
                            <span className={`milestone-text ${milestone.completed ? 'completed' : ''}`}>
                              {milestone.title}
                            </span>
                            {milestone.targetDate && (
                              <span className="milestone-date">
                                <Calendar size={12} />
                                {formatDate(milestone.targetDate)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="goal-actions">
                      <button
                        className="goal-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditGoal(goal);
                        }}
                      >
                        <Edit2 size={14} />
                        编辑
                      </button>
                      <button
                        className="goal-action-btn delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('确定要删除这个目标吗？')) {
                            onDeleteGoal(goal.id);
                          }
                        }}
                      >
                        <Trash2 size={14} />
                        删除
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="sidebar">
        <div className="card checkin-card">
          <h2 className="card-title">
            <MessageSquare size={20} />
            每周签到
          </h2>
          <div className="checkin-prompt">
            <p className="checkin-question">{currentQuestion}</p>
            <textarea
              className="checkin-textarea"
              placeholder="写下你的想法..."
              value={checkinText}
              onChange={(e) => setCheckinText(e.target.value)}
            />
          </div>
          <button
            className="btn btn-outline"
            onClick={handleCheckinSubmit}
            disabled={!checkinText.trim()}
            style={{ width: '100%', justifyContent: 'center' }}
          >
            <Sparkles size={16} />
            提交签到
          </button>
        </div>

        <div className="card" style={{ marginTop: '1rem' }}>
          <h2 className="card-title">
            <Clock size={20} />
            进度时间线
          </h2>
          {timelineEvents.length === 0 ? (
            <div className="empty-state" style={{ padding: '1.5rem' }}>
              <p style={{ fontSize: '0.85rem' }}>暂无活动记录</p>
            </div>
          ) : (
            <div className="timeline">
              {timelineEvents.map((event, index) => (
                <div key={index} className="timeline-item">
                  <div className={`timeline-dot ${event.type === 'milestone_completed' ? 'completed' : ''} ${index === 0 ? 'current' : ''}`} />
                  <div className="timeline-content">
                    <span className="timeline-date">{formatDate(event.date)}</span>
                    <h4 className="timeline-title">{event.title}</h4>
                    {event.description && (
                      <p className="timeline-desc">{event.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
